import React, { PropsWithChildren } from 'react';
import { useCookiebot } from 'hooks/useCookiebot';

/**
 * Gates the rendering of an externally hosted video behind a user's cookie consent options
 */
export const GatedVideoEmbed = ({ children }: PropsWithChildren<{}>) => {
  const cookiebot = useCookiebot();

  if (!cookiebot || !cookiebot.consent.marketing) {
    return (
      <figure className="w-full">
        <div
          className="relative w-full overflow-hidden bg-gray pt-[56.25%]"
          role="img"
          aria-label="Video cannot be loaded unless marketing cookies are accepted"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-1/2 w-1/2 items-center justify-center rounded-2xl bg-gray-dark">
              <span className="-mt-[0.35em] text-4xl leading-none tracking-[0.08em] text-white lg:text-5xl" aria-hidden="true">
                ⋯
              </span>
            </div>
          </div>
        </div>

        <figcaption className="px-4 py-4 text-center text-sm sm:text-base">
          The video player can't load with your current cookie preferences. Please{' '}
          <button type="button" className="font-medium text-gold-darker hover:underline" onClick={(_) => cookiebot?.renew()}>
            accept marketing cookies
          </button>{' '}
          to view this video content.
        </figcaption>
      </figure>
    );
  }

  return children;
};

type VideoProps = {
  url: string;
  videotype: string;
  title: string | undefined;
};

const Video = ({ url, videotype, title }: VideoProps) => (
  <GatedVideoEmbed>
    {videotype === 'vimeo' ? (
      <div className="relative pt-[56.25%]">
        <iframe
          title={title}
          src={url}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute top-0 left-0 h-full w-full"
        />
      </div>
    ) : (
      <figure className="aspect-w-16 aspect-h-9">
        <iframe
          title={title}
          src={url}
          className=""
          loading="lazy"
          allowFullScreen={true}
          style={{ border: 0 }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
        />
      </figure>
    )}
  </GatedVideoEmbed>
);

export default Video;
